'use client'

import { ArrowRight, Brain, Link2, RefreshCw, Sparkles, Zap } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import { Suspense, useEffect } from 'react'
import { AIDemoChat } from '../components/home/AIDemoChat'
import { AnimatedCounter } from '../components/home/AnimatedCounter'
import { ComparisonTable } from '../components/home/ComparisonTable'
import { DotPattern } from '../components/home/DotPattern'
import { HeroAIDemo } from '../components/home/HeroAIDemo'
import { LandingFAQ } from '../components/home/LandingFAQ'
import { Footer } from '../components/layout/Footer'
import { Header } from '../components/layout/Header'
import { HowItWorks } from '../components/layout/HowItWorks'

function CodeRedirect() {
  const searchParams = useSearchParams()
  const router = useRouter()
  const code = searchParams.get('code')

  useEffect(() => {
    if (code) {
      router.replace(`/auth/callback?code=${code}`)
    }
  }, [code, router])

  return null
}

const WHY_CARDS = [
  {
    icon: Brain,
    gradient: 'from-blue-500 to-blue-700',
    title: 'Motor de Intenção',
    description:
      'Você diz o que quer em linguagem natural — nossa IA entende. Sem checkboxes infinitos, sem formulários cansativos. Pergunta cirúrgica só quando algo está realmente ambíguo.',
  },
  {
    icon: RefreshCw,
    gradient: 'from-violet-500 to-purple-700',
    title: 'Cérebro Tecnológico Catalogando',
    description:
      'Por trás de cada busca, milhões de vagas processadas por IA em tempo real — emprego, estágio, freelancer, oportunidades regionais. Cobertura nacional com inteligência local.',
  },
  {
    icon: Link2,
    gradient: 'from-cyan-500 to-teal-700',
    title: 'Transparência Total',
    description:
      'Não escondemos a fonte. Cada vaga mostra o site de origem. Você vai direto pra candidatura no site da empresa — sem intermediário, sem taxa, sem pegadinha.',
  },
]

const LIVE_STATS = [
  {
    emoji: '🎯',
    target: 1247000,
    suffix: '+',
    label: 'vagas indexadas',
  },
  {
    emoji: '🏢',
    target: 8500,
    suffix: '+',
    label: 'empresas ativas',
  },
  {
    emoji: '📡',
    target: 50,
    suffix: '+',
    label: 'fontes monitoradas',
  },
]

export default function HomePage() {
  return (
    <div className="flex flex-col min-h-[100dvh]">
      <Suspense fallback={null}>
        <CodeRedirect />
      </Suspense>
      <Header />

      <main className="flex-1">
        {/* ====== HERO ====== */}
        <section className="relative py-16 md:py-24 lg:py-32 overflow-hidden bg-slate-950 min-h-[560px] md:min-h-[680px] lg:min-h-[760px]">
          {/* Background image — cérebro IA + categorias (decorativo) */}
          <div
            className="absolute inset-0 -z-20"
            style={{
              backgroundImage: "url('/landing-hero-brain.webp')",
              backgroundSize: 'contain',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundColor: '#020617',
            }}
            aria-hidden="true"
          />
          {/* Overlay gradient pra contraste de texto */}
          <div
            className="absolute inset-0 -z-10 bg-gradient-to-b from-slate-950/85 via-slate-950/60 to-slate-950/90"
            aria-hidden="true"
          />
          {/* Glow halo central radial sutil */}
          <div
            className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_center,rgba(59,130,246,0.18),transparent_60%)]"
            aria-hidden="true"
          />

          <div className="container mx-auto px-4 sm:px-6 text-center relative z-10">
            {/* Badge */}
            <div className="animate-in-up">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 px-3 md:px-4 py-1 md:py-1.5 text-[10px] md:text-xs font-semibold uppercase tracking-widest text-blue-200 mb-5 md:mb-8 shadow-lg">
                <Sparkles className="h-3 w-3 md:h-3.5 md:w-3.5" aria-hidden="true" />
                Inteligência Artificial em busca de vagas
              </span>
            </div>

            {/* H1 */}
            <h1 className="text-[length:var(--step-5)] font-extrabold tracking-tight text-white mb-4 md:mb-6 animate-in-up-delay-1 leading-[1.1] drop-shadow-[0_2px_8px_rgba(0,0,0,0.4)]">
              A{' '}
              <span className="bg-gradient-to-r from-blue-300 via-cyan-300 to-violet-300 bg-clip-text text-transparent">
                próxima geração
              </span>
              <br />
              de busca de vagas
            </h1>

            <p className="max-w-2xl mx-auto text-base sm:text-lg md:text-xl text-slate-200/90 mb-8 md:mb-12 animate-in-up-delay-2 leading-relaxed px-2 drop-shadow-[0_1px_4px_rgba(0,0,0,0.4)]">
              Nossa IA entende o que você quer antes de procurar.{' '}
              <span className="font-semibold text-white">
                Encontre vagas relevantes em segundos
              </span>{' '}
              — sem precisar saber filtrar.
            </p>

            {/* Interactive search demo */}
            <div className="animate-in-up-delay-3">
              <HeroAIDemo />
            </div>
          </div>
        </section>

        {/* ====== LIVE COUNTERS ====== */}
        <section
          className="py-12 md:py-16 border-y border-gray-100 dark:border-white/5 bg-white/80 dark:bg-slate-950/80 backdrop-blur-sm"
          aria-label="Estatísticas em tempo real"
        >
          <div className="container mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl mx-auto text-center">
              {LIVE_STATS.map((stat) => (
                <div key={stat.label} className="flex flex-col items-center gap-1">
                  <span className="text-2xl mb-1" aria-hidden="true">
                    {stat.emoji}
                  </span>
                  <span className="text-3xl md:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight tabular-nums">
                    <AnimatedCounter target={stat.target} suffix={stat.suffix} />
                  </span>
                  <span className="text-sm text-slate-500 dark:text-slate-400 font-medium">
                    {stat.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ====== HOW IT WORKS ====== */}
        <HowItWorks />

        {/* ====== IA CATEGORIAS VISUAL ====== */}
        <section
          className="relative py-16 md:py-24 overflow-hidden bg-gradient-to-b from-slate-50 to-white dark:from-slate-950 dark:to-zinc-950"
          aria-labelledby="ia-visual-heading"
        >
          {/* Glow decorativo */}
          <div
            className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(59,130,246,0.08),transparent_70%)] pointer-events-none"
            aria-hidden="true"
          />

          <div className="container mx-auto px-4 sm:px-6 relative z-10">
            <div className="text-center max-w-2xl mx-auto mb-8 md:mb-12">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 border border-blue-100 dark:bg-blue-950/30 dark:border-blue-900/50 px-3 py-1 text-[10px] md:text-xs font-semibold uppercase tracking-widest text-blue-600 dark:text-blue-300 mb-3 md:mb-4">
                Inteligência por trás
              </span>
              <h2
                id="ia-visual-heading"
                className="text-2xl md:text-3xl lg:text-4xl font-bold mb-3 tracking-tight text-slate-900 dark:text-white"
              >
                Um cérebro tecnológico catalogando o Brasil
              </h2>
              <p className="text-slate-600 dark:text-slate-400 text-base md:text-lg leading-relaxed">
                Emprego, estágio, freelancer, oportunidades regionais — milhões de dados processados
                em tempo real por IA, organizados por categoria, atualizados sempre.
              </p>
            </div>
          </div>

          {/* Imagem full-bleed acompanhando largura da tela */}
          <div className="relative w-full mt-8 md:mt-12">
            <Image
              src="/landing-ia-categorias.webp"
              alt="Cérebro IA do AcheiVagas catalogando categorias: emprego, hospedagem, estacionamento, restaurante, creche, consulta médica, frechel, curso, evento, aula particular, outros, concurso"
              width={2816}
              height={1536}
              className="w-full h-auto"
              sizes="100vw"
              priority={false}
            />
          </div>
        </section>

        {/* ====== WHY ACHEIVAGAS ====== */}
        <section
          className="py-16 md:py-24 relative overflow-hidden bg-gradient-to-b from-white via-blue-50/30 to-white dark:from-zinc-950 dark:via-slate-900/20 dark:to-zinc-950"
          aria-labelledby="why-heading"
        >
          <div className="container mx-auto px-4 sm:px-6">
            <div className="text-center max-w-2xl mx-auto mb-10 md:mb-16">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 border border-blue-100 dark:bg-blue-950/30 dark:border-blue-900/50 px-3 py-1 text-[10px] md:text-xs font-semibold uppercase tracking-widest text-blue-600 dark:text-blue-300 mb-3 md:mb-4">
                Diferenciais
              </span>
              <h2
                id="why-heading"
                className="text-2xl md:text-3xl lg:text-4xl font-bold mb-3 tracking-tight text-slate-900 dark:text-white"
              >
                Por que AcheiVagas é diferente
              </h2>
              <p className="text-slate-600 dark:text-slate-400 text-base md:text-lg leading-relaxed">
                Tecnologia que trabalha pra você — não contra você.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {WHY_CARDS.map((card) => (
                <article
                  key={card.title}
                  className="group rounded-2xl border border-gray-200/80 dark:border-white/10 bg-white dark:bg-slate-900 p-6 md:p-7 shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
                >
                  <div
                    className={`w-12 h-12 rounded-xl bg-gradient-to-br ${card.gradient} flex items-center justify-center shadow-md mb-5`}
                    aria-hidden="true"
                  >
                    <card.icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2 tracking-tight">
                    {card.title}
                  </h3>
                  <p className="text-sm md:text-base text-slate-600 dark:text-slate-400 leading-relaxed">
                    {card.description}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ====== AI DEMO CHAT ====== */}
        <AIDemoChat />

        {/* ====== COMPARISON TABLE ====== */}
        <ComparisonTable />

        {/* ====== FAQ ====== */}
        <LandingFAQ />

        {/* ====== FINAL CTA ====== */}
        <section className="py-20 md:py-28 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0f172a] via-slate-900 to-[#0f172a]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(37,99,235,0.15),transparent_60%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(124,58,237,0.1),transparent_60%)]" />

          {/* Concentric circles */}
          <div
            className="hidden md:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full border border-white/5"
            aria-hidden="true"
          />
          <div
            className="hidden md:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full border border-white/5"
            aria-hidden="true"
          />
          <div
            className="hidden md:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[200px] h-[200px] rounded-full border border-white/5"
            aria-hidden="true"
          />

          <DotPattern className="opacity-[0.02] text-white" />

          <div className="container mx-auto px-4 sm:px-6 text-center relative z-10">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-blue-300 mb-4 md:mb-6">
              <Zap className="h-3 w-3" aria-hidden="true" />
              Comece agora
            </span>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-3 md:mb-4 tracking-tight">
              Comece grátis. Cadastre em 30s.
            </h2>
            <p className="text-white/60 max-w-lg mx-auto mb-8 md:mb-10 text-base md:text-lg px-2">
              3 cliques por dia, buscas ilimitadas. Sem cartão de crédito. Sem pegadinhas.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              <Link
                href="/auth/register"
                className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-violet-500 hover:from-blue-600 hover:to-violet-600 text-white font-bold px-7 py-3.5 rounded-xl transition-all shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 group text-sm md:text-base"
              >
                Criar conta grátis
                <ArrowRight
                  className="h-4 w-4 group-hover:translate-x-0.5 transition-transform"
                  aria-hidden="true"
                />
              </Link>
              <Link
                href="/auth/login"
                className="flex items-center gap-2 border border-white/20 hover:border-white/40 text-white/80 hover:text-white font-semibold px-7 py-3.5 rounded-xl transition-all backdrop-blur-sm text-sm md:text-base hover:bg-white/5"
              >
                Já tenho conta — Entrar
              </Link>
            </div>

            <p className="text-white/30 text-xs mt-6">Gratuito. Sem spam. Cancele quando quiser.</p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  )
}
